class Timer {
  /**
   * @typedef Time
   * @property {number} days
   * @property {number} hours
   * @property {number} minutes
   * @property {number} seconds
   */

  /**
   * @type {?number}
   */
  end = null;

  /**
   * @type {?number}
   */
  #now = null;

  /**
   * @type {?HTMLElement}
   */
  timerElement = null;

  /**
   * @type {?number}
   */
  interval = null;

  defaultParams = {
    textColor: '#FFFFFF',
    backgroundColor: '#2C2C2C',
  };

  constructor(elementId, params = {}) {
    this.container = document.getElementById(elementId);

    if (!this.container) {
      return console.error(`Cannot find element "#${elementId}"`);
    }

    params = { ...this.defaultParams, ...params };

    if (!params.end) {
      return console.error(`End date must be specified for timer #${elementId} to work`);
    }

    this.end = params.end;
    this.#now = params.now ? params.now : new Date().getTime();

    this.textColor = params.textColor;
    this.backgroundColor = params.backgroundColor;

    const time = this.getRemainingTime();

    if (this.isTimeRemaining(time)) {
      this.createElement(time);
      this.start();
    } else {
      console.info(`No time remaining for timer #${elementId}`);
    }
  }

  /**
   * @param {Time} time
   * @returns {boolean}
   */
  isTimeRemaining(time) {
    return Object.values(time).some((t) => t > 0);
  }

  /**
   * @param {Time} time
   * @returns {string}
   */
  getHtmlTimeDuration(time) {
    return `P${time.days}DT${time.hours}H${time.minutes}M${time.seconds}S`;
  }

  /**
   * @returns Time
   */
  getRemainingTime() {
    const remainingTime = this.end - this.#now;

    return {
      days: remainingTime > 0 ? Math.floor(remainingTime / (1000 * 60 * 60 * 24)) : 0,
      hours: remainingTime > 0 ? Math.floor(remainingTime / (1000 * 60 * 60) % 24) : 0,
      minutes: remainingTime > 0 ? Math.floor(remainingTime / 1000 / 60 % 60) : 0,
      seconds: remainingTime > 0 ? Math.floor(remainingTime / 1000 % 60) : 0,
    };
  }

  /**
   * @param {Time} time
   */
  createElement(time) {
    this.container.innerHTML = `
            <timer class="timer" 
                   role="timer" 
                   datetime="${this.getHtmlTimeDuration(time)}"
                   style="background-color: ${this.backgroundColor}; color: ${this.textColor}"
            >
                <span class="timer__digits">${time.days}</span>
                <span class="timer__digits">${time.hours}</span>
                <span class="timer__digits">${time.minutes}</span>
                <span class="timer__digits">${time.seconds}</span>
                <span class="timer__letters">${BX.message('TIMER_DAYS')}</span>
                <span class="timer__letters">${BX.message('TIMER_HOURS')}</span>
                <span class="timer__letters">${BX.message('TIMER_MINUTES')}</span>
                <span class="timer__letters">${BX.message('TIMER_SECONDS')}</span>
            </timer>
        `;
    this.timerElement = this.container.firstElementChild;
  }

  /**
   * @param {Time} time
   */
  updateElement(time) {
    const [days, hours, minutes, seconds] = this.timerElement.children;

    this.timerElement.setAttribute('datetime', this.getHtmlTimeDuration(time));
    days.textContent = time.days.toString();
    hours.textContent = time.hours.toString();
    minutes.textContent = time.minutes.toString();
    seconds.textContent = time.seconds.toString();
  }

  start() {
    this.interval = setInterval(() => this.tick(), 1000);
  }

  tick() {
    this.#now = Date.now();

    const time = this.getRemainingTime();

    this.updateElement(time);

    if (!this.isTimeRemaining(time)) {
      this.stop();
      this.timerElement.setAttribute('hidden', '');
    }
  }

  stop() {
    clearInterval(this.interval);
  }
}
